import type {
  ProposalCustomer,
  ProposalDetails,
} from '@/services/quotes/types';
import { cleanCnpjOrCpf } from '@/shared/utils/document.util';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { resolveCapitalGlobalIndividualDocumentFromApi } from '@/features/quotes/utils/resolve-capital-global-id-type.util';
import type { SubStipulantEntry } from '../../types';

export type CapitalGlobalStep1Prefill = {
  insuranceType: 'new' | 'renewal';
  congenere: string;
  policyNumber: string;
  useIndividualDocument: boolean;
  documentNumber: string;
  cpf: string;
  name: string;
  businessSegment: string;
  businessCnae: string;
  averagePartnerAge?: number;
  averageEmployeeAge?: number;
  partnerCount?: number;
  employeeCount?: number;
  subStipulants: SubStipulantEntry[];
};

export function capitalGlobalStep1ValuesFingerprint(
  v: CapitalGlobalStep1Prefill
): string {
  return JSON.stringify({
    insuranceType: v.insuranceType,
    congenere: v.congenere,
    policyNumber: v.policyNumber,
    useIndividualDocument: v.useIndividualDocument,
    documentNumber: v.documentNumber,
    cpf: v.cpf,
    name: v.name,
    businessSegment: v.businessSegment,
    businessCnae: v.businessCnae,
    averagePartnerAge: v.averagePartnerAge,
    averageEmployeeAge: v.averageEmployeeAge,
    partnerCount: v.partnerCount,
    employeeCount: v.employeeCount,
    subStipulants: (v.subStipulants ?? []).map(s => ({
      sequenceNumber: s.sequenceNumber,
      documentNumber: s.documentNumber,
      cpf: s.cpf,
      name: s.name,
      useIndividualDocument: s.useIndividualDocument,
      segment: s.segment,
      cnae: s.cnae,
      averagePartnerAge: s.averagePartnerAge,
      averageEmployeeAge: s.averageEmployeeAge,
      partnerCount: s.partnerCount,
      employeeCount: s.employeeCount,
    })),
  });
}

export function capitalGlobalStep1PrefillFingerprint(
  proposalDetails: ProposalDetails
): string {
  return capitalGlobalStep1ValuesFingerprint(
    mapProposalDetailsToCapitalGlobalStep1(proposalDetails)
  );
}

function pickCustomerDocumentRaw(customer: ProposalCustomer | undefined): {
  rawCpf: string;
  rawIdNo: string;
} {
  if (!customer) return { rawCpf: '', rawIdNo: '' };

  const registerExtra = String(
    (customer as ProposalCustomer & { registerNumber?: string })
      .registerNumber ?? ''
  ).trim();

  const docNumber = String(customer.documentNumber ?? '').trim();

  let rawCpf = (customer.cpf || '').trim();
  let rawIdNo = (customer.idNo || '').trim();

  if (!rawCpf && !rawIdNo && docNumber) {
    const d = cleanCnpjOrCpf(docNumber);
    if (d.length > 11) rawIdNo = docNumber;
    else rawCpf = docNumber;
  }

  if (!rawCpf && !rawIdNo && registerExtra) {
    const d = cleanCnpjOrCpf(registerExtra);
    if (d.length > 11) rawIdNo = registerExtra;
    else rawCpf = registerExtra;
  }

  const cleanCpf = cleanCnpjOrCpf(rawCpf);
  const cleanIdNo = cleanCnpjOrCpf(rawIdNo);
  const cleanReg = cleanCnpjOrCpf(registerExtra);

  if (
    cleanReg.length > 0 &&
    cleanReg.length > Math.max(cleanCpf.length, cleanIdNo.length)
  ) {
    if (cleanReg.length > 11) rawIdNo = registerExtra;
    else rawCpf = registerExtra;
  }

  return { rawCpf, rawIdNo };
}

function mapProposalCustomerToStep1Identity(
  customer: ProposalCustomer | undefined
): {
  useIndividualDocument: boolean;
  documentNumber: string;
  name: string;
} {
  if (!customer) {
    return {
      useIndividualDocument: false,
      documentNumber: '',
      name: '',
    };
  }

  const { rawCpf, rawIdNo } = pickCustomerDocumentRaw(customer);

  const cleanCpf = cleanCnpjOrCpf(rawCpf);
  const cleanIdNo = cleanCnpjOrCpf(rawIdNo);

  const useIdNoDocument =
    cleanIdNo.length > 11 || (cleanCpf.length === 0 && cleanIdNo.length > 0);
  const rawDocument = (useIdNoDocument ? rawIdNo : rawCpf || rawIdNo).trim();
  const cleanDocument = useIdNoDocument ? cleanIdNo : cleanCpf || cleanIdNo;
  const formattedDocument =
    cleanDocument && formatRegisterNumber(cleanDocument)
      ? formatRegisterNumber(cleanDocument)
      : rawDocument;

  const docType = String(customer.documentType ?? '').trim();
  const useIndividualDocument = resolveCapitalGlobalIndividualDocumentFromApi(
    docType,
    cleanDocument,
    customer.isOrgParty
  );

  return {
    useIndividualDocument,
    documentNumber: (formattedDocument || rawDocument).trim(),
    name: (customer.name || '').trim(),
  };
}

function listSubStipulantCustomers(
  proposalDetails: ProposalDetails
): ProposalCustomer[] {
  const list = proposalDetails.customers;
  if (!list?.length) return [];

  return list
    .filter(c => c.sequenceNumber !== undefined && c.sequenceNumber !== 0)
    .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0));
}

function stableSubStipulantId(customer: ProposalCustomer): string {
  if (typeof customer.policyElementId === 'number') {
    return `sub-pe-${customer.policyElementId}`;
  }
  if (typeof customer.pk === 'number') {
    return `sub-pk-${customer.pk}`;
  }
  return `sub-seq-${customer.sequenceNumber ?? 'x'}`;
}

function mapProposalCustomerToSubStipulantEntry(
  customer: ProposalCustomer
): SubStipulantEntry {
  const identity = mapProposalCustomerToStep1Identity(customer);
  return {
    id: stableSubStipulantId(customer),
    sequenceNumber: customer.sequenceNumber,
    useIndividualDocument: identity.useIndividualDocument,
    documentNumber: identity.documentNumber,
    cpf: (customer.cpf || '').trim(),
    name: identity.name,
    segment: (customer.segment || '').trim(),
    cnae: (customer.cnae || '').trim(),
    averagePartnerAge: customer.averagePartnerAge,
    averageEmployeeAge: customer.averageEmployeeAge,
    partnerCount: customer.partnerCount,
    employeeCount: customer.employeeCount,
  };
}

export function mapProposalDetailsToCapitalGlobalStep1(
  proposalDetails: ProposalDetails
): CapitalGlobalStep1Prefill {
  const customer = proposalDetails.customer;
  const principal = mapProposalCustomerToStep1Identity(customer);

  const typeRaw =
    proposalDetails.typeOfInsurance || proposalDetails.policyType || '1';
  const isRenewal = String(typeRaw) === '3';

  const subStipulants = listSubStipulantCustomers(proposalDetails).map(
    mapProposalCustomerToSubStipulantEntry
  );

  return {
    insuranceType: isRenewal ? 'renewal' : 'new',
    congenere: (proposalDetails.previousInsurer || '').trim(),
    policyNumber: (proposalDetails.previousPolicy || '').trim(),
    useIndividualDocument: principal.useIndividualDocument,
    documentNumber: principal.documentNumber,
    cpf: (customer?.cpf || '').trim(),
    name: principal.name,
    businessSegment: (customer?.segment || '').trim(),
    businessCnae: (customer?.cnae || '').trim(),
    averagePartnerAge: customer?.averagePartnerAge,
    averageEmployeeAge: customer?.averageEmployeeAge,
    partnerCount: customer?.partnerCount,
    employeeCount: customer?.employeeCount,
    subStipulants,
  };
}
